import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { UserNotification } from '../../common/domain/models/user_notification';

export interface PendingNotification {
  id: string;
  title: string | null;
  message: string | null;
  link?: string | null;
  navigate?: string | null;
  linkDownload?: string | null;
  source: 'api' | 'login';
}

interface NotificationsState {
  owner: string | null;
  notifications: PendingNotification[];
  seenIds: string[];
  readIds: string[];
  finalizedIds: string[];
  setOwner: (owner: string) => void;
  sync: (items: UserNotification[]) => number;
  addLoginNotifications: (items: { mensagemAlerta: string | null; urlLink: string | null }[]) => number;
  markAsRead: (id: string) => void;
  finalize: (id: string) => void;
  finalizeAll: () => void;
  clear: () => void;
}

const emptyState = { owner: null, notifications: [], seenIds: [], readIds: [], finalizedIds: [] };

export const useNotificationsStore = create<NotificationsState>()(persist((set, get) => ({
  ...emptyState,
  setOwner: (owner) => {
    if (get().owner !== owner) set({ ...emptyState, owner });
  },
  sync: (items) => {
    const current = get();
    // Compatibility with API deployments that do not yet return a database ID.
    const normalized = [...new Map(items.map((item): [string, PendingNotification] => {
      const id = `api:${item.id ?? JSON.stringify([item.titulo, item.descricao])}`;
      return [id, { id, title: item.titulo, message: item.descricao, navigate: item.navigate?.trim() || null, linkDownload: item.linkDownload?.trim() || null, source: 'api' }];
    })).values()];
    const activeIds = new Set(normalized.map((item) => item.id));
    const newCount = normalized.filter((item) => !current.seenIds.includes(item.id) && !current.readIds.includes(item.id) && !current.finalizedIds.includes(item.id)).length;
    set({
      notifications: [
        ...current.notifications.filter((item) => item.source === 'login'),
        ...normalized.filter((item) => !current.finalizedIds.includes(item.id)),
      ],
      seenIds: [...current.seenIds.filter((id) => !id.startsWith('api:')), ...activeIds],
      readIds: current.readIds.filter((id) => !id.startsWith('api:') || activeIds.has(id)),
    });
    return newCount;
  },
  addLoginNotifications: (items) => {
    const current = get();
    const unique = [...new Map(items.map((item): [string, PendingNotification] => {
      const id = `login:${JSON.stringify([item.mensagemAlerta, item.urlLink])}`;
      return [id, { id, title: 'Alerta Pendente', message: item.mensagemAlerta, navigate: item.urlLink?.trim() || null, source: 'login' }];
    })).values()].filter((item) => !current.seenIds.includes(item.id) && !current.finalizedIds.includes(item.id));
    set({ notifications: [...current.notifications, ...unique], seenIds: [...current.seenIds, ...unique.map((item) => item.id)] });
    return unique.length;
  },
  markAsRead: (id) => set((state) => ({
    readIds: [...new Set([...state.readIds, id])],
  })),
  finalize: (id) => set((state) => ({
    notifications: state.notifications.filter((item) => item.id !== id),
    readIds: state.readIds.filter((readId) => readId !== id),
    finalizedIds: [...new Set([...state.finalizedIds, id])],
  })),
  finalizeAll: () => set((state) => ({
    notifications: [],
    readIds: [],
    finalizedIds: [...new Set([...state.finalizedIds, ...state.notifications.map((item) => item.id)])],
  })),
  clear: () => set(emptyState),
}), {
  name: 'pending-user-notifications',
  storage: createJSONStorage(() => sessionStorage),
  version: 3,
  migrate: (persistedState) => {
    const previous = persistedState as Partial<NotificationsState> & { dismissedIds?: string[] };
    return {
      owner: previous.owner ?? null,
      notifications: (previous.notifications ?? []).map((notification) => ({
        ...notification,
        navigate: notification.navigate?.trim() || notification.link?.trim() || null,
      })),
      seenIds: previous.seenIds ?? [],
      readIds: previous.readIds ?? previous.dismissedIds ?? [],
      finalizedIds: previous.finalizedIds ?? [],
    };
  },
  partialize: ({ owner, notifications, seenIds, readIds, finalizedIds }) => ({ owner, notifications, seenIds, readIds, finalizedIds }),
}));
